/**
 * Las opciones de un select y cómo se filtran al tipear.
 *
 * Vive aparte del componente para poder probar el filtrado sin montar nada: es
 * lo que decide qué opciones quedan cuando alguien escribe «lego» o «ingenieria»
 * en un select con acentos, mayúsculas y nombres largos.
 */

import type { ChangeEvent } from 'react'

export interface OpcionDeSelect {
  value: string
  label: string
  /** Encabezado bajo el que se agrupa la opción (lo que era un `optgroup`). */
  group?: string
  disabled?: boolean
}

/**
 * Sin acentos, sin mayúsculas y sin espacios de sobra: «Ingeniería» se
 * encuentra con «ingenieria», y «INGENIERÍA » también.
 */
export const normalizarTexto = (texto: string): string =>
  texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()

/**
 * Las opciones que quedan para lo que se escribió.
 *
 * Cada palabra tiene que aparecer en la etiqueta, en cualquier orden: «sr pro»
 * encuentra «SR. PROJECT MANAGER» y «proyecto lego» encuentra «Lego (proyecto)».
 * Sin texto devuelve todas, en su orden.
 */
export const filtrarOpciones = <T extends OpcionDeSelect>(
  opciones: readonly T[],
  consulta: string,
): T[] => {
  const palabras = normalizarTexto(consulta).split(/\s+/).filter(Boolean)
  if (palabras.length === 0) return [...opciones]

  return opciones.filter((opcion) => {
    const etiqueta = normalizarTexto(opcion.label)
    return palabras.every((palabra) => etiqueta.includes(palabra))
  })
}

export interface GrupoDeOpciones<T extends OpcionDeSelect> {
  group?: string
  items: T[]
}

/** Las opciones juntas por grupo, conservando el orden en que llegaron. */
export const agruparOpciones = <T extends OpcionDeSelect>(
  opciones: readonly T[],
): GrupoDeOpciones<T>[] => {
  const grupos: GrupoDeOpciones<T>[] = []
  for (const opcion of opciones) {
    const actual = grupos.find((g) => g.group === opcion.group)
    if (actual) actual.items.push(opcion)
    else grupos.push({ group: opcion.group, items: [opcion] })
  }
  return grupos
}

/**
 * A qué opción se mueve la selección con una flecha, saltando las
 * deshabilitadas. Da la vuelta en los extremos: la lista es un anillo.
 */
export const siguienteActiva = (
  opciones: readonly OpcionDeSelect[],
  actual: number,
  paso: 1 | -1,
): number => {
  if (opciones.length === 0) return -1
  for (let intento = 1; intento <= opciones.length; intento += 1) {
    const candidato =
      (((actual + paso * intento) % opciones.length) + opciones.length) % opciones.length
    if (!opciones[candidato].disabled) return candidato
  }
  return -1
}

/**
 * Adapta el valor que entrega el select a un manejador pensado para el evento
 * de un `<select>` nativo (`e.target.name`, `e.target.value`). Sirve para
 * migrar pantallas con un `handleChange` genérico sin reescribirlo.
 */
export const comoEventoDeCambio = (name: string, value: string) =>
  ({
    target: { name, value, type: 'select-one' },
    currentTarget: { name, value, type: 'select-one' },
  }) as unknown as ChangeEvent<HTMLSelectElement>
